import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNotify } from "react-admin";
import axios from "axios";
import httpClient from "../../lib/httpClient";
import { Grid, Box, Card, Checkbox, FormControlLabel } from "@material-ui/core";
import VisibilityOffRoundedIcon from "@material-ui/icons/VisibilityOffRounded";
import VisibilityRoundedIcon from "@material-ui/icons/VisibilityRounded";
import LocationOffIcon from "@material-ui/icons/LocationOff";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import RemoveShoppingCartIcon from "@material-ui/icons/RemoveShoppingCart";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import { getUserPermissionsList } from "../util/permissionsUtils";
import { operationCancelled } from "../../lib/httpClient";

import {
  tableCellRender,
  dateFilterOperators,
  stringFilterOperators,
  isSingleSelectOperators,
  formatDateValue,
  getCompanyCodeName,
  getLatestUpdateDaily,
  filterModelToQueryValue,
  getCompanyCodeFilter,
} from "../util/tableUtils";
import { goToPolicyPage } from "./utils";

import { UniversalMap } from "../map/UniversalMap";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { UpdateMapButton } from "../util/UpdateMapButton";

import { apiError } from "../../lib/apiError";
import { saveUserMonitorData } from "../util/userUtils";
import { DownloadTableButton } from "../util/DownloadTableButton";
import { useClientConfig } from "../../context/clientConfigContext";
import { HazardPermissionsEnum } from "../util/types/permissions/hazardPermissionsEnum";

export const PolicyList = (props) => {
  const notify = useNotify();
  const policySecondaryId = props.match.params.id2;
  const policyCompanyId = props.match.params.company;

  const { t } = useTranslation();
  const config = useClientConfig();

  const [policiesData, setPoliciesData] = useState([]);
  const [selectedLocationsAddresses, setSelectedLocationsAddresses] = useState([]);
  const [selectedContractsAddresses, setSelectedContractsAddresses] = useState([]);
  const [locationActiveFlagChecked, setLocationActiveFlagChecked] = useState(true);
  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const initialFilterModel = (policySecondaryId, policyCompanyId) => {
    if (policySecondaryId && policyCompanyId) {
      return {
        items: [
          {
            field: "policyNumberSecondary",
            operator: "equals",
            value: policySecondaryId,
            id: 1, // Necessary to have unique id for each filter;
            // otherwise DataGrid will create its own ids, and it triggers an extra request to the server.
          },
          {
            field: "companyCode",
            operator: "is",
            value: policyCompanyId,
            id: 2,
          },
        ],
      };
    } else {
      return { items: [] };
    }
  };

  const [filterModel, setFilterModel] = useState(initialFilterModel(policySecondaryId, policyCompanyId));
  const [sortModel, setSortModel] = useState({});
  const [filteredMapData, setFilteredMapData] = useState(null);

  const [lastUpdateDatePoliciesMsg, setLastUpdateDatePoliciesMsg] = useState("");
  const policiesCancelTokenRef = useRef(null);

  const onFilterChange = useCallback((aFilterModel) => {
    setFilterModel(aFilterModel);
  }, []);

  const onSortChange = useCallback((aSortModel) => {
    setSortModel(aSortModel);
  }, []);

  useEffect(() => {
    getLatestUpdateDaily("/serviceLogs/buildings?service=db_buildings_unique_multi").then((lastUpdateMsg) => {
      setLastUpdateDatePoliciesMsg(lastUpdateMsg);
    });
    saveUserMonitorData("policyList", "");
  }, []);

  const filterModelWithLocationActiveFlag = locationActiveFlagChecked
    ? {
        ...filterModel,
        items: [
          ...filterModel.items,
          {
            field: "locationActiveFlag",
            operator: "equals",
            value: "TRUE",
          },
        ],
      }
    : filterModel;

  const getPoliciesData = useCallback(async () => {
    setTableState((old) => ({ ...old, isLoading: true }));
    let sortField, sortDirection;
    const sortItem = sortModel;
    if (sortItem && Object.keys(sortItem).length) {
      const { field, sort } = sortItem[0];
      sortField = field;
      sortDirection = sort;
    }

    try {
      const { data: policiesResponse } = await httpClient().get(
        `/climatedb?page=${paginationModel.page}&pageSize=${
          paginationModel.pageSize
        }&filters=${filterModelToQueryValue(
          filterModelWithLocationActiveFlag,
        )}&orderBy=${sortField}&orderDirection=${sortDirection}`,

        {
          cancelToken: policiesCancelTokenRef.current.token,
        },
      );

      const policiesWithSelection = policiesResponse.data.map((newRow) => {
        const selectedCurrLocation = selectedLocationsAddresses.find(
          (selectedLocation) => selectedLocation.multilocationID === newRow.multilocationID,
        );
        const selectedCurrContract = selectedContractsAddresses.find(
          (selectedContract) => selectedContract.policyNumber === newRow.policyNumber,
        );

        return {
          ...newRow,
          selected: !!selectedCurrLocation,
          selectedContract: !!selectedCurrContract,
        }; // make sure selected policies are marked in the table after changing pages
      });

      setPoliciesData(policiesWithSelection);

      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: policiesResponse.total,
      }));
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        apiError(error);

        setPoliciesData([]);
        setTableState((old) => ({
          ...old,
          isLoading: false,
          total: 0,
        }));
      }
    }
  }, [filterModel, sortModel, paginationModel.page, paginationModel.pageSize, locationActiveFlagChecked]);

  useEffect(() => {
    const getPoliciesDataWrapper = async () => {
      // Cancel the previous request when the filterModel changes
      if (policiesCancelTokenRef.current) {
        policiesCancelTokenRef.current.cancel(operationCancelled);
      }

      // Create a new cancel token for the current request
      policiesCancelTokenRef.current = axios.CancelToken.source();
      getPoliciesData();
    };

    if (filterModel) {
      const isAnyIncompleteFilter = filterModel?.items?.some((filter) => !filter?.value);

      if (!isAnyIncompleteFilter) {
        getPoliciesDataWrapper();
      }
    } else {
      getPoliciesDataWrapper();
    }
  }, [filterModel, getPoliciesData]);

  const handleLocationActiveFlagChange = (event) => {
    setLocationActiveFlagChecked(event.target.checked);
    // If you need to reload data based on this flag:
    // getPoliciesData();
  };

  const downloadPoliciesTableFunction = async () => {
    try {
      let sortField, sortDirection;
      const sortItem = sortModel;
      if (sortItem && Object.keys(sortItem).length) {
        const { field, sort } = sortItem[0];
        sortField = field;
        sortDirection = sort;
      }

      const { data: policiesResponse } = await httpClient().get(
        `/climatedb?page=0&pageSize=9999999&filters=${filterModelToQueryValue(
          filterModelWithLocationActiveFlag,
        )}&orderBy=${sortField}&orderDirection=${sortDirection}`,
      );

      return policiesResponse.data;
    } catch (error) {
      apiError(error);
      return [];
    }
  };

  const onLocationCheckboxChange = async (event, selectedRow) => {
    if (event.target.checked) {
      // it's becoming selected
      let response;
      try {
        response = await httpClient().get(
          `/climatedb/${selectedRow.companyCode}/${selectedRow.multilocationID}/locationAddress`,
        );
      } catch (error) {
        console.error("error", error.response.data.message);
        notify("You encountered an internal error, please try again later.(PL1)");
        return;
      }

      const policyAddress = {
        ...response.data,
        companyCode: selectedRow.companyCode,
        locationStartDate: selectedRow.locationStartDate,
      };

      const isObjectInList = selectedLocationsAddresses.some((obj) => obj === policyAddress);
      if (!isObjectInList) setSelectedLocationsAddresses((old) => [...old, policyAddress]);
    } else {
      const currPoliciesList = selectedLocationsAddresses.filter(
        (pol) => pol.multilocationID !== selectedRow.multilocationID,
      );

      setSelectedLocationsAddresses(currPoliciesList);
    }

    const updatedData = policiesData.map((pol) => {
      if (pol._id === selectedRow._id) {
        return {
          ...pol,
          selected: !selectedRow.selected,
        };
      }
      return pol;
    });

    setPoliciesData(updatedData);
  };

  const onContractCheckboxChange = async (event, selectedRow) => {
    const isChecked = event.target.checked;

    let response = { data: [] };
    try {
      setTableState((old) => ({
        ...old,
        isLoading: true,
      }));

      response = await httpClient().get(
        `/climatedb/${selectedRow.policyNumber}/contractAddresses?filters=${filterModelToQueryValue(
          filterModelWithLocationActiveFlag,
        )}`,
      );
    } catch (error) {
      console.error("error", error.response.data.message);
      alert(error.response.data.message);
    } finally {
      setTableState((old) => ({
        ...old,
        isLoading: false,
      }));
    }

    if (isChecked) {
      const selectedContractAddressesAccumulating = selectedContractsAddresses;
      for (const policyAddress of response.data) {
        const isObjectInList = selectedContractAddressesAccumulating.some((obj) => obj === policyAddress);
        if (!isObjectInList) selectedContractAddressesAccumulating.push(policyAddress);
      }

      setSelectedContractsAddresses((old) => selectedContractAddressesAccumulating);
    } else {
      const currContractsList = selectedContractsAddresses.filter(
        (pol) => pol.policyNumber !== selectedRow.policyNumber,
      );

      setSelectedContractsAddresses(currContractsList);
    }

    const updatedData = policiesData.map((pol) => {
      if (response.data.some((address) => address._id === pol._id)) {
        return {
          ...pol,
          selectedContract: !selectedRow.selectedContract,
        };
      }
      return pol;
    });

    setPoliciesData(updatedData);
  };

  const policiesColumns = [
    {
      field: "selected",
      width: 60,
      headerName: t("policy.showLocationOnMap"),
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div
            onClick={(e) => e.stopPropagation()}
            title={t("policy.showLocationOnMapTooltip", { multilocationID: params.row.multilocationID })}
          >
            <FormControlLabel
              control={
                <Checkbox
                  icon={<LocationOffIcon />}
                  checkedIcon={<LocationOnIcon />}
                  checked={params.row?.selected}
                  onChange={(event) => onLocationCheckboxChange(event, params.row)}
                  color="primary"
                />
              }
            />
          </div>
        );
      },
    },

    {
      field: "selectedContract",
      width: 60,
      headerName: t("policy.showContractOnMap"),
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div
            onClick={(e) => e.stopPropagation()}
            title={t("policy.showContractOnMapTooltip", {
              policyNumberSecondary: params.row.policyNumberSecondary,
            })}
          >
            <FormControlLabel
              control={
                <Checkbox
                  icon={<VisibilityOffRoundedIcon />}
                  checkedIcon={<VisibilityRoundedIcon />}
                  checked={params.row?.selectedContract}
                  onChange={(event) => onContractCheckboxChange(event, params.row)}
                  color="primary"
                />
              }
            />
          </div>
        );
      },
    },

    {
      field: "companyCode" || "company",
      headerName: t("company"),
      width: 60,
      type: "singleSelect",
      filterOperators: isSingleSelectOperators,
      valueOptions: getCompanyCodeFilter(config.companyCodesMap),
      valueGetter: (params) => getCompanyCodeName(params.row.companyCode, config.companyCodesMap),
    },

    {
      field: "year",
      headerName: t("coverage.year"),
      width: 60,
    },

    {
      field: "referenceDate",
      headerName: t("coverage.yearMonth"),
      width: 80,
      hide: true,
    },

    {
      field: "policyNumberSecondary",
      headerName: t("policy.id"),
      width: 130,
    },
    {
      field: "policyNumber",
      headerName: t("policy.primaryKey"),
      hide: true,
    },
    {
      field: "multilocationID",
      headerName: t("idLocation"),
      width: 170,
    },

    {
      field: "locationActiveFlag",
      headerName: t("policy.locationActive"),
      width: 70,
      type: "singleSelect",
      filterOperators: isSingleSelectOperators,
      valueOptions: [
        { value: "TRUE", label: "TRUE" },
        { value: "FALSE", label: "FALSE" },
      ],
    },

    {
      field: "locationStartDate",
      headerName: t("policy.locationStartDate"),
      type: "date",
      filterOperators: dateFilterOperators,
      valueGetter: (params) => params.row.locationStartDate && formatDateValue(params.row.locationStartDate),
      width: 100,
    },

    {
      field: "locationEndDate",
      headerName: t("policy.locationEndDate"),
      type: "date",
      filterOperators: dateFilterOperators,
      valueGetter: (params) => params.row.locationEndDate && formatDateValue(params.row.locationEndDate),
      width: 100,
    },

    {
      field: "productCommercial",
      headerName: t("product"),
      width: 200,
    },
    {
      field: "addressCity",
      headerName: t("city"),
      width: 150,
    },
    {
      field: "addressCityCode",
      headerName: t("cityCode"),
      width: 70,
    },
    {
      field: "addressStreetName",
      headerName: t("address"),
      width: 150,
      valueGetter: (params) =>
        tableCellRender(
          `${params.row.addressType ? params.row.addressType : ""} ${
            params.row.addressStreetName ? params.row.addressStreetName : ""
          } ${params.row.addressBuildingNo ? params.row.addressBuildingNo : ""}`,
        ),
    },
    {
      field: "addressZIPCode",
      headerName: t("zip"),
      width: 70,
    },
    { field: "addressProvince", headerName: t("state"), width: 80 },
    {
      field: "inceptionDate",
      headerName: t("inceptionDate"),
      type: "date",
      filterOperators: dateFilterOperators,
      valueGetter: (params) => params.row.inceptionDate && formatDateValue(params.row.inceptionDate),
    },
    {
      field: "expiryDate",
      headerName: t("expireDate"),
      type: "date",
      filterOperators: dateFilterOperators,
      valueGetter: (params) => params.row.expiryDate && formatDateValue(params.row.expiryDate),
    },
    {
      field: "agency",
      headerName: t("agency"),

      valueGetter: (params) => `${params.row.agencyCode} ${params.row.agencyName}`,
    },
    ...(config.tableColumns.coinsuranceType
      ? [
          {
            field: "coinsuranceType",
            headerName: t("coinsuranceType"),
            type: "singleSelect",
            width: 130,

            filterOperators: isSingleSelectOperators,
            valueOptions: [
              { value: "01 - NO COASSI", label: "01 - NO COASSI" },
              {
                value: "02 - COASSI DELEGA NOSTRA",
                label: "02 - COASSI DELEGA NOSTRA",
              },
            ],
          },
        ]
      : []),

    {
      field: "addressCityCodeAdjusted",
      headerName: t("cityCode") + "+",
      width: 70,
      renderCell: (params) => {
        const { addressCityCodeAdjusted, addressCityCode } = params.row;
        const same =
          addressCityCodeAdjusted !== undefined &&
          addressCityCode !== undefined &&
          addressCityCodeAdjusted === addressCityCode;
        const color = same ? "green" : "red";
        return <span style={{ color }}>{addressCityCodeAdjusted ?? "-"}</span>;
      },
    },
  ];

  const columns = policiesColumns.map((tableColumn) => ({
    valueFormatter: (params) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,
    //flex: 1,
    ...tableColumn,
  }));

  return (
    <Grid style={{ marginTop: 25 }}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12}>
          <Box>
            <Card>
              <UniversalMap
                markers={[...selectedLocationsAddresses, ...selectedContractsAddresses]}
                page="buildings-policies"
                filteredMapDataProp={filteredMapData}
                setFilteredMapDataProp={setFilteredMapData}
                routeNameProp="policies"
              />
            </Card>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <div style={{ height: 700, width: "100%" }}>
            <DataGridCystellarTable
              columns={columns}
              rows={policiesData}
              rowCount={tableState.total}
              getRowId={(rowData) => rowData._id}
              onRowClick={(rowData) =>
                goToPolicyPage(
                  rowData.row.policyNumber,
                  rowData.row.policyNumberSecondary,
                  rowData.row.multilocationID,
                  rowData.row.locationStartDate,
                )
              }
              loading={tableState.isLoading}
              paginationMode={"server"}
              paginationModel={paginationModel}
              onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
              filterMode={"server"}
              onFilterModelChange={onFilterChange}
              sortingMode={"server"}
              onSortModelChange={onSortChange}
              customToolbarComp={true}
              customToolbarCompMsg={lastUpdateDatePoliciesMsg}
              customToolbarWidgets={
                <>
                  <UpdateMapButton
                    setFilteredMapData={setFilteredMapData}
                    filterModel={filterModelWithLocationActiveFlag}
                    routeName="policies"
                  />

                  {getUserPermissionsList().includes(HazardPermissionsEnum.Super) && (
                    <DownloadTableButton
                      downloadTableFunction={downloadPoliciesTableFunction}
                      total={tableState.total}
                      fileName="Contratti"
                    ></DownloadTableButton>
                  )}

                  <FormControlLabel
                    style={{ marginLeft: 10 }}
                    control={
                      <Checkbox
                        color="secondary"
                        size="small"
                        checked={locationActiveFlagChecked}
                        disabled={tableState.isLoading}
                        inputProps={{ "aria-label": "secondary checkbox" }}
                        onChange={handleLocationActiveFlagChange}
                      />
                    }
                    label={t("policy.locationActiveOnly")}
                  />
                </>
              }
              filterModel={filterModel} // Set the initial filter model from component props
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    // Hide columns status and traderName, the other columns will remain visible
                    policyNumber: false,
                    referenceDate: false,
                    locationActiveFlag: false,
                    locationStartDate: false,
                    locationEndDate: false,
                    addressCityCodeAdjusted: false,
                  },
                },
              }}
            />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};
